import React, { useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquare, Star, Lightbulb, Sparkles, ThumbsUp, Send, Loader2, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import CollapsiblePanel from "./CollapsiblePanel";

const CATEGORIES = [
  { id: "review", icon: Star, label: "Оценка", labelEn: "Review", color: "#F59E0B", hasRating: true },
  { id: "feedback", icon: ThumbsUp, label: "Отзив", labelEn: "Feedback", color: "#10B981" },
  { id: "suggestion", icon: MessageSquare, label: "Предложение", labelEn: "Suggestion", color: "#0EA5E9" },
  { id: "innovation", icon: Lightbulb, label: "Иновация", labelEn: "Innovation", color: "#8B5CF6" },
];

export default function FeedbackPanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [category, setCategory] = useState(null);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // iter1114 — opt-in to showcase this review publicly on the Landing page.
  const [allowPublish, setAllowPublish] = useState(false);

  const submit = async () => {
    if (!category) return;
    if (!text.trim() && rating === 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        category: category.id,
        text: text.trim(),
        rating: category.hasRating ? rating : undefined,
        allow_publish: category.id === "review" ? allowPublish : false,
      });
      toast.success(bg ? "Благодарим за обратната връзка!" : "Thank you for your feedback!");
      setSent(true);
      setTimeout(() => { setSent(false); setCategory(null); setText(""); setRating(0); setAllowPublish(false); }, 3000);
    } catch {
      toast.error(bg ? "Грешка при изпращане" : "Error submitting");
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <Card className={isDark ? 'bg-card border-border' : 'card-elevated'} data-testid="feedback-panel">
        <CardContent className="py-6 text-center">
          <div className="w-10 h-10 rounded-full bg-[#10B981]/15 flex items-center justify-center mx-auto mb-2">
            <Check className="w-5 h-5 text-[#10B981]" />
          </div>
          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1A1A2E]'}`}>
            {bg ? "Благодарим!" : "Thank you!"}
          </p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {bg ? "Вашето мнение е ценно за нас" : "Your opinion is valuable to us"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <CollapsiblePanel
      title={bg ? "Споделете мнение" : "Share your thoughts"}
      subtitle={bg ? "Помогнете ни да направим Nutramilo по-добър" : "Help us make Nutramilo better"}
      icon={Sparkles}
      iconColor="#F59E0B"
      credits={0}
      defaultOpen={false}
      storageKey="feedback-panel"
      testId="feedback-panel"
    >
      <div className="space-y-2.5">
        {/* Category pills */}
        <div className="flex flex-wrap gap-1.5" data-testid="feedback-categories">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const isActive = category?.id === cat.id;
            return (
              <button key={cat.id} onClick={() => { setCategory(cat); setRating(0); }}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                  isActive
                    ? `text-white shadow-sm`
                    : isDark ? 'bg-[hsl(223,47%,15%)] text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                style={isActive ? { backgroundColor: cat.color } : {}}
                data-testid={`feedback-cat-${cat.id}`}
              >
                <Icon className="w-3 h-3" />
                {bg ? cat.label : cat.labelEn}
              </button>
            );
          })}
        </div>

        {category && (
          <>
            {/* Star rating for reviews */}
            {category.hasRating && (
              <div className="flex items-center gap-1" data-testid="feedback-rating">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s}
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(s)}
                    className="p-0.5 transition-transform hover:scale-110"
                    data-testid={`star-${s}`}
                  >
                    <Star className={`w-5 h-5 ${(hoverRating || rating) >= s ? 'text-[#F59E0B] fill-[#F59E0B]' : isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  </button>
                ))}
                {rating > 0 && (
                  <span className={`text-[10px] ml-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {rating}/5
                  </span>
                )}
              </div>
            )}

            {/* Text input */}
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={bg
                ? category.id === "review" ? "Какво ви харесва или не? (незадължително)"
                  : category.id === "suggestion" ? "Какво бихте подобрили?"
                  : category.id === "innovation" ? "Какви нови функции бихте искали?"
                  : "Споделете вашия опит..."
                : "Share your thoughts..."
              }
              className={`w-full h-16 p-2 text-xs rounded-lg border resize-none ${
                isDark ? 'bg-[hsl(223,47%,15%)] border-[hsl(223,47%,20%)] text-white placeholder:text-gray-600' : 'border-gray-200 placeholder:text-gray-400'
              }`}
              data-testid="feedback-text"
            />

            {/* iter1114 — public-showcase opt-in (reviews only) */}
            {category.id === "review" && (
              <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="feedback-allow-publish-row">
                <input type="checkbox" className="mt-0.5"
                  checked={allowPublish}
                  onChange={(e) => setAllowPublish(e.target.checked)}
                  data-testid="feedback-allow-publish"
                />
                <span className={`text-[10px] leading-snug ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {bg
                    ? "Съгласен/-на съм отзивът ми да се покаже публично на главната страница (само лично име, без електронна поща). Можете да го оттеглите по всяко време от Профил → Отзиви."
                    : "I agree to showcase this review publicly on the Landing page (first name only, no email). You can revoke it anytime from Profile → Reviews."}
                </span>
              </label>
            )}

            {/* Submit */}
            <Button size="sm" className="w-full h-8 text-xs"
              style={{ backgroundColor: category.color }}
              onClick={submit}
              disabled={sending || (!text.trim() && rating === 0)}
              data-testid="feedback-submit"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
              {bg ? "Изпрати" : "Submit"}
            </Button>
          </>
        )}
      </div>
    </CollapsiblePanel>
  );
}
